import { useCallback, useRef, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

/**
 * Stato React persistito in **sessionStorage** (non localStorage): i filtri e
 * la ricerca di una pagina sono contesto di lavoro della sessione corrente, non
 * una preferenza permanente. sessionStorage vive quanto la scheda del browser →
 * navigando fra le pagine dell'app lo stato sopravvive al remount (item 9
 * Davide "cambiando pagina si resetta tutto"), ma un giorno nuovo (scheda
 * chiusa e riaperta) riparte pulito dai default, senza trascinarsi filtri
 * dimenticati da settimane prima. localStorage li renderebbe eterni.
 *
 * Drop-in di ``useState``: ritorna ``[value, setValue]`` con lo STESSO
 * contratto — ``setValue`` accetta un valore o un updater funzionale — così i
 * chiamanti passano ``setValue`` come prop ai figli senza wrapper, e
 * ``handleShowAll``/``handleClearSearch`` persistono automaticamente perché
 * passano dal setter.
 *
 * Set/Map non sono JSON-serializzabili (``JSON.stringify(new Set())`` → "{}"):
 * per quei tipi passare ``opts`` con serialize/deserialize espliciti — vedi
 * ``stringSetCodec``.
 */
export interface PersistedStateOptions<T> {
  serialize?: (value: T) => string;
  deserialize?: (raw: string) => T;
}

function readPersisted<T>(key: string, initial: T, deserialize: (raw: string) => T): T {
  try {
    const raw = window.sessionStorage.getItem(key);
    if (raw === null) return initial;
    return deserialize(raw);
  } catch {
    // sessionStorage assente/negato (private mode) o valore corrotto: si parte
    // dal default, senza crashare il primo render.
    return initial;
  }
}

export function usePersistedState<T>(
  key: string,
  initial: T,
  opts?: PersistedStateOptions<T>,
): [T, Dispatch<SetStateAction<T>>] {
  const serialize = opts?.serialize ?? ((value: T) => JSON.stringify(value));
  const deserialize = opts?.deserialize ?? ((raw: string) => JSON.parse(raw) as T);

  // key + serialize catturati in ref: un ``opts`` inline cambia identità a ogni
  // render, ma il setter deve restare stabile (passato come prop ai figli come
  // una useState nativa) → niente deps volatili nella useCallback, niente
  // re-render a cascata sulle card figlie.
  const keyRef = useRef(key);
  keyRef.current = key;
  const serializeRef = useRef(serialize);
  serializeRef.current = serialize;

  // deserialize serve solo al lazy init (gira una volta sola): niente ref.
  const [value, setValue] = useState<T>(() => readPersisted(key, initial, deserialize));

  const setPersisted = useCallback<Dispatch<SetStateAction<T>>>((update) => {
    setValue((prev) => {
      const next = typeof update === "function" ? (update as (p: T) => T)(prev) : update;
      try {
        window.sessionStorage.setItem(keyRef.current, serializeRef.current(next));
      } catch {
        // Storage pieno/negato: lo stato vive comunque in memoria, semplicemente
        // non sopravvive alla navigazione. Persistenza best-effort, non un dato
        // di dominio → nessun throw.
      }
      return next;
    });
  }, []);

  return [value, setPersisted];
}

/** Codec Set<string> ⇄ JSON array. JSON non serializza i Set (→ "{}"), quindi
 *  passiamo per array; il filtro su ``typeof x === "string"`` scarta i valori
 *  corrotti senza rompere il Set. Riusato da activeChips e supplierFilter. */
export const stringSetCodec: PersistedStateOptions<Set<string>> = {
  serialize: (set) => JSON.stringify([...set]),
  deserialize: (raw) => {
    const parsed: unknown = JSON.parse(raw);
    return new Set(
      Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === "string") : [],
    );
  },
};
